import { migrateDiceCandidate } from './candidatesApi';
import { resolveDiceProfileId } from './diceCandidateMapper';

export function normalizeSourceKey(value) {
  if (value && typeof value === 'object') {
    return normalizeSourceKey(value.value ?? value.key ?? value.id ?? value.label ?? value.name);
  }

  return String(value ?? '').trim().toLowerCase();
}

export function getSearchSources(value) {
  return (Array.isArray(value) ? value : [value])
    .flat()
    .map(normalizeSourceKey)
    .filter((item, index, source) => item && item !== 'all' && source.indexOf(item) === index);
}

export function resolveSearchSource(value, fallback = 'internal') {
  const sources = getSearchSources(value);
  if (sources.includes('internal')) return 'internal';
  const normalizedFallback = normalizeSourceKey(fallback);
  if (sources[0]) return sources[0];
  if (normalizedFallback) return normalizedFallback;
  return fallback === '' ? '' : 'internal';
}

function toTitle(value) {
  return String(value ?? '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTabKey(tab) {
  return normalizeSourceKey(tab?.key ?? tab?.value ?? tab?.id ?? tab?.source ?? tab?.name ?? tab?.title ?? tab?.label);
}

function getTabCount(tab) {
  const count = tab?.count ?? tab?.countValue;
  const number = Number(count);
  return Number.isFinite(number) ? number : undefined;
}

function getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount) {
  if (source === activeSource) {
    const responseCount = Number(responseCounts?.[source]);
    if (Number.isFinite(responseCount)) return responseCount;

    const activeTotal = Number(activeCount);
    if (Number.isFinite(activeTotal)) return activeTotal;
  }

  const currentCount = currentCounts.get(source);
  if (currentCount !== undefined) return currentCount;

  return undefined;
}

export function buildSourceTabs({
  sources = [],
  activeSource = 'internal',
  activeCount = 0,
  responseCounts = {},
  currentTabs = [],
  backendTabs = [],
} = {}) {
  const currentCounts = new Map(
    (Array.isArray(currentTabs) ? currentTabs : [])
      .map((tab) => [getTabKey(tab), getTabCount(tab)])
      .filter(([source, count]) => source && count !== undefined),
  );

  const sourceMap = new Map();

  (Array.isArray(backendTabs) ? backendTabs : []).forEach((tab) => {
    const key = getTabKey(tab);
    if (!key) return;
    sourceMap.set(key, {
      key,
      title: tab.title ?? tab.label ?? tab.name ?? toTitle(key),
      ...(getTabCount(tab) !== undefined ? { count: getTabCount(tab) } : {}),
    });
  });

  const selectedSources = getSearchSources(sources);
  (selectedSources.length ? selectedSources : [activeSource]).forEach((source) => {
    if (!source || sourceMap.has(source)) return;
    const count = getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount);
    sourceMap.set(source, {
      key: source,
      title: toTitle(source),
      ...(count !== undefined ? { count } : {}),
    });
  });

  return Array.from(sourceMap.values());
}

function collectSourceValues(record) {
  return [
    record?.sourceType,
    record?.profileSource,
    record?.selectedSource,
    record?.source,
    record?.provider,
    record?.customFields?.sourceType,
    record?.customFields?.profileSource,
    record?.customFields?.source,
    record?.customFields?.provider,
  ].flat().map(normalizeSourceKey).filter(Boolean);
}

function recordMatchesSource(record, sourceKey, marker) {
  const normalizedSource = normalizeSourceKey(sourceKey);
  if (!normalizedSource) return false;
  if (collectSourceValues(record).includes(normalizedSource)) return true;
  return typeof marker === 'function' ? marker(record) : false;
}

const SOURCE_ACTIONS = [
  {
    source: 'dice',
    field: '__sourceAction_dice_migrate',
    label: 'Action',
    buttonLabel: 'Migrate',
    completedLabel: 'Migrated',
    successMessage: 'Candidate migrated successfully.',
    errorMessage: 'Failed to migrate candidate.',
    isRecordFromSource: (record) => Boolean(record?.customFields?.diceProfileData),
    getRecordId: resolveDiceProfileId,
    execute: migrateDiceCandidate,
  },
];

export function getAvailableSourceActions({ rows = [], selectedSource = '' } = {}) {
  return SOURCE_ACTIONS.filter((action) => {
    const selectedMatches = normalizeSourceKey(selectedSource) === action.source;
    return selectedMatches || rows.some((record) => recordMatchesSource(record, action.source, action.isRecordFromSource));
  });
}

export function sourceActionAppliesToRecord(action, record, selectedSource = '') {
  return normalizeSourceKey(selectedSource) === action.source
    || recordMatchesSource(record, action.source, action.isRecordFromSource);
}

export function getSourceActionInstanceKey(action, record) {
  const id = action?.getRecordId?.(record);
  return id ? `${action.source}:${action.field}:${id}` : '';
}

export async function runSourceAction(action, record) {
  const id = action?.getRecordId?.(record);
  if (!id) throw new Error(`${action?.buttonLabel ?? 'Action'} ID is required`);
  return action.execute(id, record);
}
